<template>
  <v-dialog v-model="visible" scrollable persistent max-width="800" @keydown.esc="cancelForm()">
    <v-card class="dealer-form-card">
      <!-- Header de la tarjeta con barra de herramientas -->
      <v-toolbar color="primary" dark>
        <v-btn icon @click="cancelForm()" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-toolbar-title>{{ title }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn :loading="loading" :disabled="!validations.isValid" @click="saveForm()" icon class="mr-2">
          <v-icon>mdi-content-save</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Contenido principal del formulario -->
      <v-card-text class="pa-0">
        <v-container fluid>
          <v-form ref="dealerForm" v-model="validations.isValid">
            <v-row>
              <!-- Sección izquierda - Información principal -->
              <v-col cols="12" md="6">
                <v-card outlined class="mb-4">
                  <v-card-title class="subtitle-1 font-weight-bold py-2 px-4">
                    <v-icon left color="primary">mdi-information</v-icon>
                    Información básica
                    <v-spacer></v-spacer>
                    <v-chip x-small color="red" v-if="!isNewRecord">ID: {{ fields.id }}</v-chip>
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pt-4">
                    <!-- Nombre del concesionario -->
                    <v-text-field v-model="fields.name" :rules="validations.name" :error-messages="errors.name"
                      :disabled="loading" label="Nombre del concesionario *" prepend-inner-icon="mdi-store" outlined
                      dense hide-details="auto" class="mb-4"></v-text-field>

                    <!-- Manager -->
                    <v-autocomplete v-model="selectedAdmin" :rules="validations.admin_id"
                      :error-messages="errors.admin_id" :items="userState.list" :loading="userState.loading"
                      :search-input.sync="userState.search" :disabled="loading" :filter="filterUsers" item-text="name"
                      item-value="id" label="Manager *" prepend-inner-icon="mdi-account-tie" hide-details="auto"
                      hide-no-data hide-selected return-object outlined dense class="mb-4">
                      <template v-slot:selection="{ item }">
                        <div class="d-flex align-center">
                          <v-avatar size="24" color="primary lighten-4" class="mr-2">
                            <v-icon small color="primary">mdi-account</v-icon>
                          </v-avatar>
                          {{ item.name }} {{ item.lastname }}
                        </div>
                      </template>
                      <template v-slot:item="{ item }">
                        <v-list-item-avatar color="primary lighten-4">
                          <v-icon color="primary">mdi-account</v-icon>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title>{{ item.name }} {{ item.lastname }}</v-list-item-title>
                          <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>

                    <!-- Email -->
                    <v-text-field v-model="fields.email" :rules="validations.email" :error-messages="errors.email"
                      :disabled="loading" label="Correo electrónico *" prepend-inner-icon="mdi-email" outlined dense
                      hide-details="auto" class="mb-4"></v-text-field>

                    <!-- Sitio web -->
                    <v-text-field v-model="fields.website" :rules="validations.website" :error-messages="errors.website"
                      :disabled="loading" label="Sitio web" prepend-inner-icon="mdi-web" outlined dense
                      hide-details="auto" class="mb-4"></v-text-field>

                    <!-- Ciudad -->
                    <v-text-field v-model="fields.city" :rules="validations.city" :error-messages="errors.city"
                      :disabled="loading" label="Ciudad" prepend-inner-icon="mdi-city" outlined dense
                      hide-details="auto"></v-text-field>
                  </v-card-text>
                </v-card>
                <!-- Sección de personalización -->
                <v-card outlined>
                  <v-card-title class="subtitle-1 font-weight-bold py-2 px-4">
                    <v-icon left color="primary">mdi-palette</v-icon>
                    Personalización
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pt-4">
                    <!-- Logo -->
                    <div class="d-flex flex-column flex-md-row mb-4">
                      <div class="logo-preview mr-md-3 mb-4 mb-md-0">
                        <v-avatar size="80" color="grey lighten-3" class="elevation-2">
                          <v-img v-if="logoPreview" :src="logoPreview" alt="Logo" contain></v-img>
                          <v-icon v-else size="40" color="grey">mdi-image-outline</v-icon>
                        </v-avatar>
                      </div>
                      <div class="upload-control flex-grow-1">
                        <v-file-input v-model="fields.logo" :disabled="loading" label="Logo del concesionario"
                          prepend-icon="mdi-camera" outlined dense accept="image/*" @change="previewLogo"
                          hide-details="auto">
                          <template v-slot:selection="{ text }">
                            <v-chip small outlined label color="primary">
                              <v-icon left small>mdi-image</v-icon>
                              {{ text }}
                            </v-chip>
                          </template>
                        </v-file-input>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Sección derecha - Contacto y personalización -->
              <v-col cols="12" md="6">
                <!-- Sección de contacto -->
                <v-card outlined class="mb-4">
                  <v-card-title class="subtitle-1 font-weight-bold py-2 px-4">
                    <v-icon left color="primary">mdi-phone</v-icon>
                    Información de contacto
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pt-4">
                    <!-- Dirección -->
                    <v-text-field v-model="fields.address" :rules="validations.address" :error-messages="errors.address"
                      :disabled="loading" label="Dirección" prepend-inner-icon="mdi-map-marker" outlined dense
                      hide-details="auto" class="mb-4"></v-text-field>

                    <!-- Teléfono -->
                    <v-text-field v-model="fields.phone" :rules="validations.phone" :error-messages="errors.phone"
                      :disabled="loading" label="Teléfono *" prepend-inner-icon="mdi-phone" outlined dense
                      hide-details="auto" class="mb-4"></v-text-field>

                    <!-- Teléfono móvil -->
                    <v-text-field v-model="fields.mobile_phone" :rules="validations.mobile_phone"
                      :error-messages="errors.mobile_phone" :disabled="loading" label="Teléfono móvil"
                      prepend-inner-icon="mdi-cellphone" outlined dense hide-details="auto"></v-text-field>
                  </v-card-text>
                </v-card>

                <!-- Tipos de vehículos -->
                <v-card outlined class="mb-4">
                  <v-card-title class="subtitle-1 font-weight-bold py-2 px-4">
                    <v-icon left color="primary">mdi-car-multiple</v-icon>
                    Tipos de vehículos
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-card-text class="pt-4">
                    <v-row dense>
                      <v-col cols="12" sm="4">
                        <v-checkbox
                          v-model="fields.vn"
                          :disabled="loading"
                          label="VN"
                          hide-details
                          color="success"
                        >
                          <template v-slot:label>
                            <span class="text-body-2">VN</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                              </template>
                              <span>Vehículo Nuevo</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <v-checkbox
                          v-model="fields.vo"
                          :disabled="loading"
                          label="VO"
                          hide-details
                          color="amber"
                        >
                          <template v-slot:label>
                            <span class="text-body-2">VO</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                              </template>
                              <span>Vehículo de Ocasión</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <v-checkbox
                          v-model="fields.taller"
                          :disabled="loading"
                          label="Taller"
                          hide-details
                          color="indigo"
                        >
                          <template v-slot:label>
                            <span class="text-body-2">Taller</span>
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
                              </template>
                              <span>Taller de reparación</span>
                            </v-tooltip>
                          </template>
                        </v-checkbox>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <!-- Barra inferior con acciones -->
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn
          :disabled="loading"
          @click="cancelForm()"
          color="grey darken-1"
          text
          class="px-4"
        >
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          :loading="loading"
          :disabled="saveIsDisabled"
          @click="saveForm()"
          color="primary"
          class="px-4"
          depressed
        >
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import UserService from '../../services/UserService'
import DealerService from '../../services/DealerService'

export default {
  props: {
    value: null
  },

  data: () => ({
    visible: true,
    loading: false,
    logoPreview: null,

    // campos del formulario
    fields: {
      id: null,
      name: null,
      address: null,
      phone: null,
      mobile_phone: null,
      email: null,
      website: null,
      logo: null,
      admin_id: null,
      // theme: null,
      city: null,
      vn: false,
      vo: false,
      taller: false
    },

    // themes: [
    //   {
    //     text: 'Claro',
    //     value: 'light',
    //     colors: [
    //       '#ffffff', '#f5f5f5', '#c0c0c0'
    //     ]
    //   }
    //   /* {
    //     text: 'Oscuro',
    //     value: 'dark',
    //     colors: [
    //       '#212121', '#424242', '#9e9e9e'
    //     ]
    //   },
    //   {
    //     text: 'Azul',
    //     value: 'blue',
    //     colors: [
    //       '#bbdefb', '#64b5f6', '#1e88e5'
    //     ]
    //   },
    //   {
    //     text: 'Verde',
    //     value: 'green',
    //     colors: [
    //       '#a5d6a7', '#66bb6a', '#388e3c'
    //     ]
    //   },
    //   {
    //     text: 'Rojo',
    //     value: 'red',
    //     colors: [
    //       '#ff5252', '#ff1744', '#d50000'
    //     ]
    //   } */
    // ],

    // validaciones

    validations: {
      isValid: true,
      name: [
        v => !!v || 'El nombre del concesionario es obligatorio'
      ],
      address: [],
      phone: [
        v => !!v || 'El teléfono es obligatorio',
        v => /^[0-9]+$/.test(v) || 'El teléfono solo puede contener números'
      ],
      mobile_phone: [
        v => !v || /^[0-9]+$/.test(v) || 'El teléfono móvil solo puede contener números'
      ],
      email: [
        v => !!v || 'El correo electrónico es obligatorio',
        v => /.+@.+\..+/.test(v) || 'Debe proporcionar un correo electrónico válido'
      ],
      website: [
        v => !v || /^(http:\/\/|https:\/\/)?(www\.)?([a-zA-Z0-9]+)\.([a-zA-Z]{2,})/.test(v) || 'Debe proporcionar una URL válida'
      ],
      logo: [],
      admin_id: [
        v => !!v || 'El manager es obligatorio'
      ],
      // theme: [],
      city: []
    },

    // errores
    errors: {
      name: [],
      address: [],
      phone: [],
      mobile_phone: [],
      email: [],
      website: [],
      logo: [],
      admin_id: [],
      // theme: [],
      city: []
    },

    userState: {
      list: [],
      loading: false,
      search: ''
    },

    selectedAdmin: null
  }),

  async created () {
    if (this.value) {
      await this.$store.dispatch('VIEW_DEALER', { ...this.value }).then(async result => {
        this.fields = Object.assign({}, this.value)
        
        console.log('Dealer value:', this.value)
        console.log('Fields after assign:', this.fields)

        // Si hay un logo existente, mostrar la vista previa
        if (this.fields.imageUrl) {
          this.fields.logo = this.fields.imageUrl
          this.logoPreview = this.fields.logo.url
        }

        if (this.fields.admin_id) {
          await UserService.viewUserDealer(this.fields.admin_id).then(({ data }) => {
            this.selectedAdmin = { ...data.data }
            const adminExists = this.userState.list.some(user => user.id === this.selectedAdmin.id)
            if (!adminExists) {
              this.userState.list.push(this.selectedAdmin) // Agrega el administrador a la lista
            }
          })
        }
      })
    }
    this.loading = false
  },

  methods: {
    saveForm () {
      this.$refs.dealerForm.validate()
      if (this.validations.isValid) {
        this.loading = true

        // Limpiar errores previos
        for (const key in this.errors) {
          this.errors[key] = []
        }

        // Asigna solo el ID del administrador seleccionado
        this.fields.admin_id = this.selectedAdmin ? this.selectedAdmin.id : null

        // Preparamos los campos excepto el logo
        const dealer = Object.assign({}, this.fields)
        delete dealer.logo // Removemos el logo del objeto principal
        
        console.log('isNewRecord:', this.isNewRecord)
        console.log('Dealer object to save:', dealer)
        console.log('Dealer ID:', dealer.id)

        // Si es un nuevo concesionario o estamos actualizando sin logo, podemos proceder normal

        this.$store.dispatch(this.isNewRecord ? 'ADD_DEALER' : 'UPDATE_DEALER', dealer).then(async (response) => {
          let { error, data, status } = response
          if (error) {
            if (status === 422) {
              // mostrando los errores de validación en los campos correspondientes
              data.forEach(({ field, message }) => {
                if (this.errors[field]) {
                  this.errors[field].push(message)
                }
              })
            } else {
              // mostrando cualquier otro error al usuario en una notificación
              console.error('Error:', data)
              this.$toast.error('Ocurrió un error al intentar registrar o actualizar el concesionario')
            }
            this.loading = false
            return
          }

          // Si hay logo, lo subimos después de crear/actualizar el concesionario
          if (this.fields.logo) {
            const formData = new FormData()
            formData.append('image', this.fields.logo)

            try {
              const logoResponse = await DealerService.addImage(data.data.id, formData)
              if (!logoResponse.data.success) {
                this.$toast.error('Error al guardar el logo')
              }
            } catch (error) {
              console.error('Error al subir el logo:', error)
              this.$toast.error('Error al subir el logo')
            }
          }

          this.$emit('save', { insert: this.isNewRecord, value: data })
          this.$toast.success(`Concesionario ${this.isNewRecord ? 'creado' : 'actualizado'} con éxito`)
          this.loading = false
        })
      }
    },

    cancelForm () {
      this.$emit('cancel')
    },

    resetFields () {
      this.fields = {
        name: null,
        address: null,
        phone: null,
        mobile_phone: null,
        email: null,
        website: null,
        logo: null,
        admin_id: null,
        // theme: null,
        city: null
      }
      this.selectedAdmin = null
      this.logoPreview = null
    },

    previewLogo (file) {
      if (file) {
        this.logoPreview = URL.createObjectURL(file)
      } else {
        this.logoPreview = null
      }
    },

    trabajadorDebouncedSearch: _.debounce(function () {
      this.userState.loading = true
      UserService.getManager({ 'filter[name]': this.userState.search }).then(({ data }) => {
        this.userState.list = data.data
        this.userState.loading = false
      }).catch(error => {
        console.error('Error al buscar managers:', error)
        this.userState.loading = false
      })
    }, 500),

    filterUsers (item, queryText, itemText) {
      const name = item.name ? item.name.toLowerCase() : ''
      const lastname = item.lastname ? item.lastname.toLowerCase() : ''
      const email = item.email ? item.email.toLowerCase() : ''
      const searchText = queryText.toLowerCase()

      return name.includes(searchText) ||
        lastname.includes(searchText) ||
        email.includes(searchText)
    }
  },

  computed: {
    isNewRecord () {
      return !this.value
    },

    title () {
      return this.isNewRecord ? 'Nuevo concesionario' : 'Modificar concesionario'
    },

    saveIsDisabled () {
      return this.loading || !this.fields.name || !this.fields.admin_id
    }
  },

  watch: {
    'userState.search': function (newSearch) {
      if (newSearch) {
        this.trabajadorDebouncedSearch()
      }
    },

    selectedAdmin: function (newAdmin) {
      this.fields.admin_id = newAdmin ? newAdmin.id : null
    }
  }
}
</script>

<style scoped>
.dealer-form-card {
  border-radius: 0;
  display: flex;
  flex-direction: column;
}

.v-card-text {
  flex-grow: 1;
  overflow-y: auto;
  padding-bottom: 75px !important;
  /* Para evitar que el contenido quede debajo del footer */
}

.v-card__title {
  font-size: 1rem;
}

.theme-color-preview {
  display: flex;
  align-items: center;
}

.logo-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 100px;
}

.v-text-field--outlined>>>.v-label {
  font-size: 0.9rem;
}

.v-footer {
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
}
</style>
